/**
 * @fileoverview Shared callable API contract for the `saveSuperSections`
 * Cloud Function, which saves a course's admin-managed Super Section records
 * and the course's `superSectionIds` index together.
 * Imported by both the functions handler and the webapp client so the two
 * sides cannot drift in name, request shape, or response shape.
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * functions codebase so deployment and invocation resolve to the same
 * function.
 */
export const SAVE_SUPER_SECTIONS_FUNCTION_NAME = 'saveSuperSections';

/**
 * Firestore's per-batch write limit. One save writes every submitted section,
 * every deletion and the course document in a single batch.
 */
export const SAVE_SUPER_SECTIONS_MAX_BATCH_WRITES = 500;

/**
 * Parses a URL string.
 *
 * @param value - The candidate URL.
 * @return The parsed URL, or undefined when `value` is not a valid URL.
 * @throws Never.
 */
function parseUrl(value: string): URL | undefined {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

/**
 * Editable fields for one Super Section meeting time submitted by an admin.
 * Blank `firstTimestamp` and `conferencingUrl` (after trimming) clear the
 * stored field.
 */
export const SuperSectionInputSchema = z.object({
  /** Client-side row id, echoed back in the response. */
  draftId: z.string().min(1),
  /** Existing section to update; omitted for a row to create. */
  sectionId: FirestoreDocumentIdSchema.optional(),
  /** First meeting as an ISO 8601 string; blank for none. */
  firstTimestamp: z
    .string()
    .refine(
      value => value.trim() === '' || Number.isFinite(Date.parse(value.trim())),
      'A Super section timestamp must be a valid date and time.',
    ),
  /** Conferencing link; blank for none, otherwise a valid `https:` URL. */
  conferencingUrl: z.string().superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      return;
    }
    const url = parseUrl(trimmed);
    if (url === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'A Super section conferencing URL must be a valid URL.',
      });
      return;
    }
    if (url.protocol !== 'https:') {
      ctx.addIssue({
        code: 'custom',
        message: 'A Super section conferencing URL must use HTTPS.',
      });
    }
  }),
});

/** See {@link SuperSectionInputSchema}. */
export type SuperSectionInput = z.infer<typeof SuperSectionInputSchema>;

/**
 * Request body of the `saveSuperSections` callable: the feature toggle, the
 * submitted rows, and the sections to delete.
 */
export const SaveSuperSectionsRequestSchema = z
  .object({
    /** Course whose Super Sections are saved. */
    courseId: FirestoreDocumentIdSchema,
    /** Whether the course lists its Super Sections in `superSectionIds`. */
    enabled: z.boolean(),
    /** Rows to create or update. */
    sections: z.array(SuperSectionInputSchema),
    /** Existing Super Sections to delete. */
    deletedSectionIds: z.array(FirestoreDocumentIdSchema),
  })
  .superRefine(({sections, deletedSectionIds}, ctx) => {
    const draftIds = new Set<string>();
    const sectionIds = new Set<string>();
    for (const section of sections) {
      if (draftIds.has(section.draftId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate Super section row.',
        });
      }
      draftIds.add(section.draftId);
      if (section.sectionId === undefined) {
        continue;
      }
      if (sectionIds.has(section.sectionId)) {
        ctx.addIssue({code: 'custom', message: 'Invalid Super section id.'});
      }
      sectionIds.add(section.sectionId);
    }

    const deletedIds = new Set<string>();
    for (const sectionId of deletedSectionIds) {
      if (deletedIds.has(sectionId) || sectionIds.has(sectionId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid deleted Super section id.',
        });
      }
      deletedIds.add(sectionId);
    }

    if (
      sections.length + deletedSectionIds.length + 1 >
      SAVE_SUPER_SECTIONS_MAX_BATCH_WRITES
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Too many Super section changes in one save.',
      });
    }
  });

/** See {@link SaveSuperSectionsRequestSchema}. */
export type SaveSuperSectionsRequest = z.infer<
  typeof SaveSuperSectionsRequestSchema
>;

/** Response body of the `saveSuperSections` callable. */
export const SaveSuperSectionsResponseSchema = z.object({
  /** Firestore ids assigned to the submitted rows, keyed by draft id. */
  sections: z.array(z.object({draftId: z.string(), sectionId: z.string()})),
});

/** See {@link SaveSuperSectionsResponseSchema}. */
export type SaveSuperSectionsResponse = z.infer<
  typeof SaveSuperSectionsResponseSchema
>;
